import Studio from 'src/models/box-office-report/Studio'
import Generic from 'src/models/generic/Generic'
import Model, { executeMutation, GqlError, ID, IsoDateTime, MODEL_LEVEL } from 'src/models/model'
import BoxOfficeReportSettlement from 'src/models/settlement/BoxOfficeReportSettlement'
export const gqlUri = process.env.GRAPHQL_HTTP_URI
export const serverUri = gqlUri.split('/graphql')[0]

const ACTIONS = {
  SUBMIT: 'submit',
  PAY: 'pay',
  SETTLE: 'settle',
  UNSETTLE: 'unsettle'
}
ACTIONS.ALL = _.values(ACTIONS)

export const STATES = {
  OPEN: 'open',
  SUBMITTED: 'submitted',
  PAID: 'paid',
  FINALIZED: 'finalized'
}
STATES.ALL = _.values(STATES)
STATES.LOCKED = [STATES.SUBMITTED, STATES.PAID, STATES.FINALIZED]

export default class StudioSettlement extends Model {
  static name = 'StudioSettlement'

  static deletable = true
  static paginate = true
  static versioned = true

  static level = MODEL_LEVEL.CIRCUIT

  static employeeRequiredPermissionCategories = [Model.permissionCategories.FilmSettlement]

  static define () {
    this.fields = {
      id: {
        type: ID,
        required: true
      },
      studio: {
        type: Studio,
        reference: true,
        output: true
      },
      state: {
        type: String,
        output: true
      },
      notes: {
        type: String,
        default: null
      },
      identifier: {
        type: String,
        output: true
      },
      description: {
        type: String,
        output: true
      },
      boxOfficeReportSettlements: {
        type: [BoxOfficeReportSettlement],
        output: true
      },

      totalDue: {
        type: Number,
        output: true
      },
      amount: {
        type: Number,
        output: true
      },
      remainingDue: {
        type: Number,
        output: true
      },

      submittedAt: {
        type: IsoDateTime,
        output: true
      },
      paidAt: {
        type: IsoDateTime,
        output: true
      },

      action: {
        type: String,
        input: true
      }
    }

    this.mutations = {
      actionStudioSettlements: {
        arguments: {
          ids: {
            type: [ID],
            required: true
          },
          action: {
            type: String,
            required: true
          }
        },
        results: {
          actionResult: Generic,
          errors: [GqlError]
        }
      }
    }
  }

  view () {
    window.open(serverUri + '/box_office_report_for_studio_settlement?id=' + this.id, '_blank')
  }

  async submit () {
    if (_.includes(STATES.LOCKED, this.state)) return

    this.action = ACTIONS.SUBMIT

    try {
      await this.save()
    } catch (error) {
      console.log(error)
      this.$rollbar.error(error)
    } finally {
      this.resetField('action')
    }
  }

  async pay () {
    if (this.state === STATES.PAID) return

    this.action = ACTIONS.PAY

    try {
      await this.save()
    } catch (error) {
      console.log(error)
      this.$rollbar.error(error)
    } finally {
      this.resetField('action')
    }
  }

  async settle () {
    if (this.state !== STATES.PAID) return

    this.action = ACTIONS.SETTLE

    try {
      await this.save()
    } catch (error) {
      console.log(error)
      this.$rollbar.error(error)
    } finally {
      this.resetField('action')
    }
  }

  async unsettle () {
    if (this.state !== STATES.FINALIZED) return

    this.action = ACTIONS.UNSETTLE

    try {
      await this.save()
    } catch (error) {
      console.log(error)
      this.$rollbar.error(error)
    } finally {
      this.resetField('action')
    }
  }

  async actionStudioSettlements (ids, action) {
    if (_.isEmpty(ids)) {
      return {
        errors: [{
          description: 'Must view at least one Studio Settlement'
        }]
      }
    }

    if (!_.includes(ACTIONS.ALL, action)) {
      return {
        errors: [{
          description: `Invalid action: ${action}`
        }]
      }
    }

    const results = await executeMutation(
      'actionStudioSettlements',
      { ids, action },
      {
        actionResult: {
          structure: {
            value: true
          }
        },
        errors: {
          structure: {
            code: true,
            description: true
          }
        }
      }
    )

    return results
  }

  async submitAll (ids) {
    return await this.actionStudioSettlements(ids, ACTIONS.SUBMIT)
  }

  async payAll (ids) {
    return await this.actionStudioSettlements(ids, ACTIONS.PAY)
  }

  async settleAll (ids) {
    return await this.actionStudioSettlements(ids, ACTIONS.SETTLE)
  }

  async unsettleAll (ids) {
    return await this.actionStudioSettlements(ids, ACTIONS.UNSETTLE)
  }
}
